import { sharedData } from '@/shares/dataManager';
import { provider } from '@/types/auth';
import { captureException } from '@sentry/browser';

const IOSLoginWebkitMessageHandler = (provider: provider, isLink?: boolean) => {
  try {
    const providers = {
      'google.com': 'google',
      'facebook.com': 'facebook',
      'apple.com': 'apple',
      'oidc.kakao': 'kakao',
      'oidc.kakao.native': 'kakao',
    };
    const supportedProviders = providers[provider];
    const isLiveApp =
      sessionStorage.getItem('isLiveApp') === 'true' ||
      sharedData.queries.isLiveApp;

    const postedMessage = JSON.stringify({
      method: 'sso',
      params: { provider: supportedProviders, isLink: isLink || false },
    });

    if (isLiveApp) {
      window?.webkit?.messageHandlers?.CameraFiLive?.postMessage(postedMessage);
      return;
    }

    window?.webkit?.messageHandlers?.CameraFiStudio?.postMessage(postedMessage);
  } catch (err) {
    captureException(err);
  }
};

const postIdTokenWebkitMessage = async (idToken: string) => {
  try {
    await window?.webkit?.messageHandlers?.CameraFiStudio?.postMessage(
      JSON.stringify({
        method: 'postIdToken',
        params: { idToken },
      }),
    );
  } catch (error) {
    captureException(error);
  }
};

const postCustomTokenWebkitMessage = async (customToken: string) => {
  try {
    await window?.webkit?.messageHandlers?.CameraFiLive?.postMessage(
      JSON.stringify({
        method: 'postCustomToken',
        params: { customToken },
      }),
    );
  } catch (error) {
    captureException(error);
  }
};

const deleteFcmToken = (idToken: string) => {
  try {
    window?.webkit?.messageHandlers?.CameraFiStudio?.postMessage(
      JSON.stringify({
        method: 'deleteFcmToken',
        params: { idToken },
      }),
    );
  } catch (error) {
    captureException(error);
  }
};

//* IOS 앱에서 로그인 시 idToken, customToken 전달 및 FCM 토큰 생성 요청 함수 실행
//* idToken은 카메라파이 스튜디오 앱(CameraFiStudio 핸들러)에서 사용
//* customToken은 카메라파이 라이브 앱(CameraFiLive 핸들러)에서 사용
//* 각 앱은 자신에게 해당하는 메세지 핸들러만 등록하므로, 두 메시지를 동시에 보내도 다른 앱에는 영향이 없음
const postTokensToIOSApp = async ({
  idToken,
  customToken,
}: {
  idToken: string;
  customToken: string;
}) => {
  await postIdTokenWebkitMessage(idToken);
  await postCustomTokenWebkitMessage(customToken);
};

//* CameraFiLive 앱 업데이트 전 유저의 경우 기존 방식대로 처리
const postAuthTerminateMessageWithLegacyFallback = (
  method: 'authTerminateCancel' | 'authTerminateSuccess',
) => {
  try {
    const handlers = window?.webkit?.messageHandlers;
    if (handlers?.CameraFiLive) {
      handlers.CameraFiLive.postMessage(
        JSON.stringify({ method, params: null }),
      );
      return;
    }
    handlers?.[method]?.postMessage({});
  } catch (error) {
    captureException(error);
  }
};

const postAuthTerminateCancelWebkitMessage = () => {
  postAuthTerminateMessageWithLegacyFallback('authTerminateCancel');
};

const postAuthTerminateSuccessWebkitMessage = () => {
  postAuthTerminateMessageWithLegacyFallback('authTerminateSuccess');
};

export {
  IOSLoginWebkitMessageHandler,
  deleteFcmToken,
  postTokensToIOSApp,
  postAuthTerminateCancelWebkitMessage,
  postAuthTerminateSuccessWebkitMessage,
};
